import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CTAButton } from "./CTAButton";
import { useLang } from "@/i18n/LanguageContext";

const PDF_URL = "/malinois-guia-whatsapp-business-agent-2026.pdf";
const PDF_FILENAME = "Malinois-Guia-WhatsApp-Business-Agent-2026.pdf";
// HubSpot Forms API — portal 246971738, form "Guía Gratis - WhatsApp Business Agent (Leads)".
// Public endpoint, CORS-enabled, no API key required (browser-safe).
const HUBSPOT_FORM_ENDPOINT =
  "https://api.hsforms.com/submissions/v3/integration/submit/246971738/d3c6dc16-0662-45d3-8139-7751d67029de";

const NAME_MIN = 2;
const PHONE_RE = /^[0-9+()\-\s]{7,}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function GuideDownloadModal({ open, onOpenChange }) {
  const { t, lang } = useLang();
  const m = t.guideModal;

  const [form, setForm] = useState({ full_name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | success

  const nameValid = form.full_name.trim().length >= NAME_MIN;
  const phoneValid = PHONE_RE.test(form.phone.trim());
  const emailValid = EMAIL_RE.test(form.email.trim());
  const valid = nameValid && phoneValid && emailValid;

  // Reset when modal closes so re-open shows a clean form.
  useEffect(() => {
    if (!open) {
      setForm({ full_name: "", phone: "", email: "" });
      setStatus("idle");
      setSubmitting(false);
    }
  }, [open]);

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = PDF_URL;
    link.download = PDF_FILENAME;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const submitToHubSpot = async (data) => {
    // Fire-and-forget CRM push. Silent-fail on network so a HubSpot outage
    // never blocks the user's PDF download.
    try {
      await fetch(HUBSPOT_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fields: [
            { name: "firstname", value: data.full_name },
            { name: "phone", value: data.phone },
            { name: "email", value: data.email },
          ],
        }),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("HubSpot form submit failed:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setStatus("idle");
    const payload = {
      full_name: form.full_name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      language: lang,
    };
    // Push to HubSpot CRM so the lead lands in the pipeline. The PDF is a
    // static file, so the download itself never depends on this — a
    // HubSpot outage costs a missed lead record, not a broken download.
    await submitToHubSpot(payload);
    triggerDownload();
    setStatus("success");
    setSubmitting(false);
  };

  const inputClass =
    "w-full bg-transparent border-0 border-b border-paper/25 focus:border-signalLime focus:outline-none py-3 font-body text-base text-paper placeholder:text-paper/40 transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="guide-download-modal"
        className="max-w-lg bg-ink text-paper border-paper/10 p-8 md:p-10 sm:rounded-none"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label={m.close}
          data-testid="guide-modal-close"
          className="absolute top-4 right-4 z-10 text-paper/60 hover:text-paper transition-colors"
        >
          <X size={20} />
        </button>

        <div className="relative z-10">
          {/* "Ya vive" (already living/active) gets a literal signal —
              a small pulsing dot in the Conversión chapter's signal color,
              the same visual language used for /servicios — instead of a
              static mono label. */}
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signalLime opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-signalLime" />
            </span>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-signalLime">
              MALINOIS · GUÍA GRATIS
            </p>
          </div>

          <DialogTitle asChild>
            <motion.h3
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="mt-4 font-heading font-semibold italic text-3xl md:text-4xl leading-[1.15] tracking-tight text-paper [text-wrap:balance]"
              data-testid="guide-modal-title"
            >
              {m.title}
            </motion.h3>
          </DialogTitle>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="mt-4 font-body text-sm md:text-base text-paper/70 leading-relaxed [text-wrap:pretty]"
          >
            {m.subtitle}
          </motion.p>

          <AnimatePresence mode="wait">
            {status === "success" ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8"
                data-testid="guide-modal-success"
              >
                <p className="font-heading italic text-xl text-paper">{m.success}</p>
                <button
                  type="button"
                  onClick={triggerDownload}
                  data-testid="guide-modal-redownload"
                  className="mt-4 font-mono text-[11px] tracking-[0.2em] uppercase text-signalLime hover:text-paper transition-colors"
                >
                  {m.submit} ↓
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={handleSubmit}
                className="mt-8 space-y-5"
                data-testid="guide-modal-form"
              >
                <input
                  type="text"
                  autoComplete="name"
                  placeholder={m.name}
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className={inputClass}
                  data-testid="guide-modal-name"
                  required
                />
                <input
                  type="tel"
                  autoComplete="tel"
                  placeholder={m.phone}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputClass}
                  data-testid="guide-modal-phone"
                  required
                />
                <input
                  type="email"
                  autoComplete="email"
                  placeholder={m.email}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputClass}
                  data-testid="guide-modal-email"
                  required
                />

                <div className="pt-2">
                  <CTAButton
                    type="submit"
                    testId="guide-modal-submit"
                    label={submitting ? m.submitting : m.submit}
                    disabled={!valid || submitting}
                  />
                </div>

                <p className="pt-2 font-mono text-[10px] tracking-[0.1em] uppercase text-paper/45">
                  {m.privacy}
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
